export type SubmitFormSpec = {
  /** The room's h1. */
  readonly title: string;
  /** One sentence under it: what gets submitted and what happens next. */
  readonly lede: string;
  /** `entry.json`'s `schema` value, exactly as the game validates it. */
  readonly entrySchema: string;
  readonly text: {
    readonly label: string;
    readonly placeholder: string;
    /** Weighted characters (X's count, `weighted.ts`); the game's hard gate. */
    readonly maxWeighted: number;
    /** Lines the counter reads when the text is over. */
    readonly overLimit: string;
  };
  readonly media: {
    readonly label: string;
    /** `accept` for the file input: exactly what the game decodes. */
    readonly accept: string;
    /** Per-format ceilings, checked against the picked file's MIME type before
     * the button enables, so a 6 MiB PNG is refused here rather than by the game
     * after an upload. A file whose type matches none of them is refused too. */
    readonly kinds: readonly {
      readonly label: string;
      readonly mimes: readonly string[];
      readonly maxBytes: number;
    }[];
    readonly hint: string;
  } | null;
  readonly altText: {
    readonly label: string;
    readonly maxChars: number;
    readonly hint: string;
  } | null;
  /** Where the submission goes once it is graded: the sentence the room prints
   * after the write lands, with the play address substituted for `{play}`. */
  readonly afterSubmit: string;
  /** Text under the lede, for the house rules; each item one short line. */
  readonly rules: readonly string[];
};
const mebibyte = 1024 * 1024;
const submitForms: Readonly<Record<string, SubmitFormSpec>> = {
  marketing: {
    title: "Submit a post",
    lede: "Write the X post the Softmax research account should publish. The judge grades it within a couple of minutes; the best unpublished post is the next one the team puts on the feed, and once it is live the audience scores the other half.",
    entrySchema: "softmax-post-entry/1",
    text: {
      label: "Post",
      placeholder:
        "One true, specific thing about the work. Dry last line. softmax.com",
      maxWeighted: 280,
      overLimit: "Over X's limit. A link counts 23; wide characters count 2.",
    },
    media: {
      label: "Picture or video (optional)",
      accept:
        "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm",
      // X's own publishing limits, mirrored in the game's `Limits` (marketing/config.py).
      kinds: [
        {
          label: "picture",
          mimes: ["image/png", "image/jpeg", "image/webp"],
          maxBytes: 5 * mebibyte,
        },
        { label: "GIF", mimes: ["image/gif"], maxBytes: 15 * mebibyte },
        {
          label: "video",
          mimes: ["video/mp4", "video/quicktime", "video/webm"],
          maxBytes: 100 * mebibyte,
        },
      ],
      hint: "PNG, JPEG or WebP up to 5 MiB, GIF up to 15 MiB, or MP4 from half a second to 140 seconds. It has to read in a feed, muted, at phone size.",
    },
    altText: {
      label: "Alt text",
      maxChars: 1000,
      hint: "One or two sentences describing the picture for people who cannot see it. The judge deducts for media without it.",
    },
    afterSubmit:
      "Your post is in. The judge grades it within a couple of minutes; the standings on {play} will show it with its score and the judge's notes.",
    rules: [
      "No hype, no hashtags as decoration, no exclamation marks, no emoji as punctuation.",
      "Say something only Softmax could say, then stop. The last line is dry.",
      "Media earns its place only if it explains itself with the sound off.",
    ],
  },
};
/** The form for a coworld, by its corpus name (`league.game.coworld_name`), or null when it takes no browser submissions. */
export function submitFormFor(
  coworldName: string | null | undefined,
): SubmitFormSpec | null {
  if (!coworldName) return null;

  return submitForms[coworldName] ?? null;
}
/** Why a picked file cannot be submitted, or null when it fits one of the form's media kinds. */
function mediaProblem({
  spec,
  file,
}: {
  readonly spec: SubmitFormSpec;
  readonly file: File;
}): string | null {
  if (!spec.media) return "This coworld takes no media.";

  const kind = spec.media.kinds.find((candidate) =>
    candidate.mimes.includes(file.type),
  );

  if (!kind)
    return `${file.type || "That file type"} is not one the game decodes. Use a PNG, JPEG, WebP, GIF or MP4.`;

  if (file.size > kind.maxBytes) {
    const limit = Math.round(kind.maxBytes / mebibyte);
    return `That ${kind.label} is ${(file.size / mebibyte).toFixed(1)} MiB; X takes a ${kind.label} up to ${limit} MiB.`;
  }

  return null;
}

export function submissionProblem({
  spec,
  text,
  media,
  weight,
  hasPlayer,
}: {
  readonly spec: SubmitFormSpec;
  readonly text: string;
  readonly media: File | null;
  readonly weight: number;
  readonly hasPlayer: boolean;
}): string | null {
  if (text.trim().length === 0 && media === null)
    return "Write the post, or attach a picture or clip, to continue.";

  if (weight > spec.text.maxWeighted) return spec.text.overLimit;

  const mediaIssue = media ? mediaProblem({ spec, file: media }) : null;

  if (mediaIssue) return mediaIssue;

  return hasPlayer ? null : "Create a player first.";
}
